import React, { useEffect, useState } from "react";
import { FaStar, FaRegStar, FaCheckCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import Navbar from "../nav_bar/Navbar";
import {
  fetchAllReviews,
  fetchReviewSummary,
  deleteMyReview,
  addOrUpdateReview,
} from "../../services/reviewService";
import AddReviewModal from "../review/AddReviewModal";

import "./ReviewsPage.css";

const ReviewsPage = () => {
  console.groupCollapsed("[ReviewsPage] Render");

  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editReview, setEditReview] = useState(null);

  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;

  console.log("Logged In:", isLoggedIn);
  console.groupEnd();

  /* ===============================
     LOAD REVIEWS
  =============================== */
  const loadReviews = async () => {
    console.group("[ReviewsPage] Loading Reviews");
    try {
      setLoading(true);

      console.log("Fetching reviews + summary...");

      const [reviewsRes, summaryRes] = await Promise.all([
        fetchAllReviews(),
        fetchReviewSummary(),
      ]);

      console.log("Reviews response:", reviewsRes.data);
      console.log("Summary response:", summaryRes.data);

      setReviews(reviewsRes.data || []);
      setAverageRating(summaryRes.data?.averageRating || 0);
      setTotalReviews(summaryRes.data?.totalReviews || 0);

      console.log("State updated successfully.");
    } catch (err) {
      console.error("Failed to load reviews:", err);
      setAlert({ message: "Failed to load reviews" });
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  useEffect(() => {
    console.log("[ReviewsPage] Component mounted");
    loadReviews();
  }, []);

  /* ===============================
     ADD / EDIT REVIEW
  =============================== */
  const handleAddReview = () => {
    console.group("[ReviewsPage] Add Review Clicked");

    if (!isLoggedIn) {
      console.warn("User not logged in. Redirecting to /login");
      navigate("/login");
      console.groupEnd();
      return;
    }

    console.log("Opening Add Review Modal");
    setEditReview(null);
    setShowModal(true);

    console.groupEnd();
  };

  const handleSubmitReview = async ({ rating, review }) => {
    console.group("[ReviewsPage] Submit Review");
    console.log("Payload:", { rating, review });

    try {
      await addOrUpdateReview({ rating, review });

      console.log("Review submitted successfully.");

      setShowModal(false);
      setEditReview(null);
      loadReviews();
    } catch (err) {
      console.error("Failed to submit review:", err);
      setAlert({ message: "Failed to submit review" });
    } finally {
      console.groupEnd();
    }
  };

  /* ===============================
     DELETE REVIEW
  =============================== */
  const handleDelete = async () => {
    console.group("[ReviewsPage] Delete Review");

    try {
      console.log("Deleting review...");
      await deleteMyReview();

      console.log("Review deleted successfully.");

      setConfirmDelete(false);
      loadReviews();
    } catch (err) {
      console.error("Failed to delete review:", err);
      setAlert({ message: "Failed to delete review" });
    } finally {
      console.groupEnd();
    }
  };

  /* ===============================
     LOADING STATE
  =============================== */
  if (loading) {
    console.log("[ReviewsPage] Loading state active");
    return (
      <>
        <Navbar />
        <p className="reviews-loading">Loading reviews...</p>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="reviews-page">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="reviews-header"
        >
          <h2>⭐ User Reviews</h2>

          <div className="rating-summary">
            {[1, 2, 3, 4, 5].map((i) =>
              i <= Math.round(averageRating) ? (
                <FaStar key={i} />
              ) : (
                <FaRegStar key={i} />
              )
            )}
            <strong>{averageRating.toFixed(1)} / 5</strong>
            <span>({totalReviews} reviews)</span>
          </div>

          <button className="add-review-btn" onClick={handleAddReview}>
            ✍️ Add your review
          </button>
        </motion.div>

        {/* LIST */}
        <div className="reviews-list">
          {reviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="review-card"
            >
              <div className="review-header">
                <div className="review-user">
                  <strong>{review.userName || "User"}</strong>
                  {review.verified && (
                    <span className="verified-badge">
                      <FaCheckCircle size={12} /> Verified
                    </span>
                  )}
                </div>
                <span className="review-date">
                  {new Date(review.createdAt).toDateString()}
                </span>
              </div>

              <div className="review-stars">
                {[1, 2, 3, 4, 5].map((i) =>
                  i <= review.rating ? (
                    <FaStar key={i} />
                  ) : (
                    <FaRegStar key={i} />
                  )
                )}
              </div>

              {review.review && (
                <p className="review-text">{review.review}</p>
              )}

              {review.mine && (
                <div className="review-actions">
                  <button
                    onClick={() => {
                      console.log(
                        "[ReviewsPage] Edit clicked:",
                        review
                      );
                      setEditReview(review);
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="danger"
                    onClick={() => {
                      console.log(
                        "[ReviewsPage] Delete confirmation opened"
                      );
                      setConfirmDelete(true);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* CONFIRM DELETE MODAL */}
      <AnimatePresence>
        {confirmDelete && (
          <div className="modal-overlay">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="modal-box"
            >
              <h3>Delete review?</h3>
              <p>This action cannot be undone.</p>
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    console.log(
                      "[ReviewsPage] Delete cancelled"
                    );
                    setConfirmDelete(false);
                  }}
                >
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* TOAST */}
      <AnimatePresence>
        {alert && (
          <div className="toast">
            {console.warn("[ReviewsPage] Alert shown:", alert.message)}
            {alert.message}
          </div>
        )}
      </AnimatePresence>

      {/* ADD / EDIT MODAL */}
      <AddReviewModal
        open={showModal}
        onClose={() => {
          console.log("[ReviewsPage] Modal closed");
          setShowModal(false);
        }}
        onSubmit={handleSubmitReview}
        initialRating={editReview?.rating || 0}
        initialComment={editReview?.review || ""}
        isEdit={!!editReview}
      />
    </>
  );
};

export default ReviewsPage;
